/**
 * One strategy, as a card — ROADMAP 5.19.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A CARD AND NOT A ROW
 *
 * A row is for a list that is SCANNED DOWN ONE COLUMN — an order book, where
 * every entry answers the same question and the eye travels vertically
 * comparing one figure. A strategy is not read that way. Choosing one means
 * reading six unlike facts about it at once (what it does, which way it
 * leans, where its tails are, how wide, how big, when it expires), and a row
 * can only give each of those a narrow column that the longest value then
 * defines for every other card.
 *
 * The thirty-leg template is the proof: in a row its chips pushed the expiry
 * and the size off to the right of a 1,400px screen, so the one strategy
 * whose shape was hardest to read also got the least room to show it.
 *
 * WHAT A CARD MAY STATE
 *
 * Only what the TEMPLATE knows — see `strategyFacts`. There is no feed on
 * this page, so no breakeven, no net credit and no max loss in rupees; those
 * need a chain and belong on the payoff dock. The facts here are true of
 * every underlying at once, which is the right scope for a template.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { StrategyDto } from '@/types/strategy'
import { legLabel, strategyFacts } from '../utils/strategyFacts'
import {
  BIAS_LABEL,
  BIAS_RAIL,
  BIAS_TAG,
  EXPIRY_LABEL,
  LEG_CHIP_LIMIT,
  PREMIUM_LABEL,
  PREMIUM_MEANING,
  RISK_CLASS,
  RISK_LABEL,
  RISK_MEANING,
} from '../constants'

/** One LABEL / value pair in the card's fact grid. */
function Fact({
  label,
  value,
  className,
  title,
  testId,
}: {
  label: string
  value: string
  className?: string
  title?: string
  testId?: string
}) {
  return (
    <div className="min-w-0" title={title}>
      <div className="text-[9.5px] font-medium uppercase tracking-[0.07em] text-muted-foreground/70">
        {label}
      </div>
      {/*
        NOT TRUNCATED. Every value here is short except one — "Open both
        ways" — and that is precisely the one a trader must never read as
        "Open both w…". It wraps to a second line and the grid row grows
        with it, which is affordable now that the card spends no rows on
        prose.
      */}
      <div
        className={cn(
          'text-[12.5px] font-medium leading-tight text-foreground',
          className,
        )}
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  )
}

export function StrategyCard({
  strategy: s,
  onEdit,
}: {
  strategy: StrategyDto
  onEdit: () => void
}) {
  const facts = strategyFacts(s)
  const shown = s.legs.slice(0, LEG_CHIP_LIMIT)
  const hidden = s.legs.slice(LEG_CHIP_LIMIT)

  return (
    <article
      className="qbar-card relative flex flex-col overflow-hidden rounded-[10px] transition-colors hover:border-primary/35"
      data-testid={`strategy-${s.templateKey}`}
    >
      {/* The bias rail. Identity while scrolling, before a single word is read. */}
      <span
        className={cn('absolute inset-y-0 left-0 w-[3px]', BIAS_RAIL[s.bias])}
        aria-hidden
      />

      {/*
        NO PROSE ON THE CARD. The construction line ("Sell OTM CE + Sell OTM
        PE") restated in words the chips directly below it, and the context
        line was long enough that every card truncated it — so it taught
        nothing and cost two rows on every card in the grid. Both still exist
        on the DTO and both are still SEARCHABLE; see `strategyFilter`. What
        a trader cannot read in a cell they can still reach by typing it.
      */}
      <header className="flex items-center gap-2 px-4 pb-2.5 pt-3">
        <h3
          className="min-w-0 flex-1 truncate text-[13.5px] font-semibold leading-tight"
          title={s.name}
        >
          {s.name}
        </h3>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.07em]',
            BIAS_TAG[s.bias],
          )}
        >
          {BIAS_LABEL[s.bias] ?? s.bias}
        </span>
        {/*
          The only action, and now the only reason for the header to have a
          right-hand side. Icon-only because the card carries exactly one
          action and the word for it would be the widest thing in the row.
        */}
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={onEdit}
          data-testid={`edit-${s.templateKey}`}
          aria-label={`Edit ${s.name}`}
          title={`Edit ${s.name}`}
          className="-mr-1 shrink-0 text-muted-foreground hover:text-foreground"
        >
          <Pencil size={12} />
        </Button>
      </header>

      {/* ── The structure itself, in the shortest honest form ──────────────── */}
      <div className="num flex flex-wrap gap-1 px-4 pb-3">
        {shown.map((leg) => (
          <span
            key={leg.id}
            className={cn(
              'rounded px-1.5 py-0.5 text-[10.5px] leading-[1.5]',
              leg.criteriaError
                ? 'bg-warning/15 text-warning'
                : leg.side === 'BUY'
                  ? 'bg-success/10 text-success'
                  : 'bg-destructive/10 text-destructive',
            )}
            title={leg.criteriaError ?? undefined}
          >
            {legLabel(leg)}
          </span>
        ))}
        {hidden.length > 0 && (
          /*
           * The rest are reachable in the editor, and on hover here. A card
           * that grew to thirty chips would bury the facts below it, which
           * are the reason the card exists.
           */
          <span
            className="rounded bg-muted px-1.5 py-0.5 text-[10.5px] leading-[1.5] text-muted-foreground"
            title={hidden.map(legLabel).join('\n')}
            data-testid={`more-legs-${s.templateKey}`}
          >
            +{hidden.length}
          </span>
        )}
      </div>

      {/* ── The six facts a template can answer without a price ────────────── */}
      <div className="mt-auto grid grid-cols-3 gap-x-3 gap-y-2.5 border-t border-border/60 px-4 py-3">
        <Fact
          label="Risk"
          value={RISK_LABEL[facts.risk]}
          className={RISK_CLASS[facts.risk]}
          title={RISK_MEANING[facts.risk]}
          testId={`risk-${s.templateKey}`}
        />
        <Fact
          label="Structure"
          value={facts.structure === 'SHORT' ? 'Short' : 'Long'}
          title={
            facts.structure === 'SHORT'
              ? 'The leg nearest the money is sold, so this is written as a short structure. Firing it BUY places it inverted.'
              : 'The leg nearest the money is bought, so this is written as a long structure. Firing it SELL places it inverted.'
          }
        />
        <Fact
          label="Premium"
          value={PREMIUM_LABEL[facts.premium]}
          title={PREMIUM_MEANING[facts.premium]}
          className={facts.premium === 'DEPENDS' ? 'text-muted-foreground' : undefined}
          testId={`premium-${s.templateKey}`}
        />
        <Fact
          label="Expiry"
          value={EXPIRY_LABEL[s.expiryKind] ?? s.expiryKind}
        />
        <Fact
          label="Size"
          value={`${s.defaultLots} lot${s.defaultLots === 1 ? '' : 's'}`}
          className="num"
        />
        <Fact
          label="Checks"
          value={s.checklist.length === 0 ? 'Off' : String(s.checklist.length)}
          title={
            s.checklist.length === 0
              ? 'No pre-trade checklist on this strategy.'
              : s.checklist.join('\n')
          }
          className={s.checklist.length === 0 ? 'text-muted-foreground' : 'num'}
        />
      </div>
    </article>
  )
}
